"use client";

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

const FOCUSABLE_SELECTOR =
  "a[href], button, input, select, textarea, [tabindex], [contenteditable='true']";

type DialogScope = { element: HTMLElement; layer: number };
const dialogStack: DialogScope[] = [];

function focusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(":disabled") &&
      !element.closest("[inert]") &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== "hidden",
  );
}

function topmostDialog(): DialogScope | undefined {
  // Explicit layer tokens take precedence over mount order: a high-layer
  // fullscreen chart must keep ownership when a lower dialog rerenders.
  return dialogStack.reduce<DialogScope | undefined>((top, scope) => {
    return scope.layer >= (top?.layer ?? -Infinity) ? scope : top;
  }, undefined);
}

/** Shared keyboard and focus behavior for Modal and opaque chart dialogs. */
export function useDialogFocus({
  open,
  dialogRef,
  onClose,
  closeDisabled = false,
  getFallbackFocusTarget,
}: {
  open: boolean;
  dialogRef: RefObject<HTMLElement | null>;
  onClose?: () => void;
  closeDisabled?: boolean;
  /** Resolve the current launch control when rendering replaced the original. */
  getFallbackFocusTarget?: () => HTMLElement | null;
}) {
  const optionsRef = useRef({ onClose, closeDisabled, getFallbackFocusTarget });
  useLayoutEffect(() => {
    optionsRef.current = { onClose, closeDisabled, getFallbackFocusTarget };
  }, [onClose, closeDisabled, getFallbackFocusTarget]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const scope = {
      element: dialog,
      layer: Number.parseInt(getComputedStyle(dialog).zIndex) || 0,
    };
    dialogStack.push(scope);

    const frame = window.requestAnimationFrame(() => {
      if (
        topmostDialog() !== scope ||
        dialog.contains(document.activeElement)
      ) return;
      const targets = focusableElements(dialog);
      const preferred = targets.find((element) =>
        element.matches("[data-autofocus], [autofocus]"),
      );
      (preferred ?? targets[0] ?? dialog).focus({ preventScroll: true });
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || topmostDialog() !== scope) return;
      if (event.key === "Escape") {
        // Consume Escape even during a save, so it cannot reach a parent.
        event.preventDefault();
        event.stopPropagation();
        const { onClose, closeDisabled } = optionsRef.current;
        if (!closeDisabled) onClose?.();
        return;
      }
      if (event.key !== "Tab") return;
      const targets = focusableElements(dialog);
      const first = targets[0];
      const last = targets[targets.length - 1];
      const active = document.activeElement;
      let next: HTMLElement | undefined;
      if (!first) {
        next = dialog;
      } else if (!dialog.contains(active) || active === dialog) {
        // A chart view can remount its toolbar and leave focus on the body.
        // Recover on the next Tab without sending the user behind the dialog.
        next = event.shiftKey ? last : first;
      } else if (event.shiftKey && active === first) {
        next = last;
      } else if (!event.shiftKey && active === last) {
        next = first;
      }
      if (next) {
        event.preventDefault();
        event.stopPropagation();
        next.focus({ preventScroll: true });
      }
    };

    // Bubble phase lets child controls consume Escape before the dialog.
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      const wasTopmost = topmostDialog() === scope;
      const index = dialogStack.indexOf(scope);
      if (index >= 0) dialogStack.splice(index, 1);
      if (wasTopmost) {
        const restoreTarget = previouslyFocused?.isConnected
          ? previouslyFocused
          : optionsRef.current.getFallbackFocusTarget?.();
        if (restoreTarget?.isConnected) {
          restoreTarget.focus({ preventScroll: true });
        }
      }
    };
  }, [open, dialogRef]);
}
